import { useRef } from "react";
import {
  motion,
  useMotionValue,
  useScroll,
  useSpring,
  useTransform,
} from "framer-motion";
import { ArrowRight } from "lucide-react";
import { IMAGES } from "@/data/content";
import { scrollToId } from "@/lib/scroll";
import { EASE, MaskedLines } from "./shared";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const copyY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [3, -3]), {
    stiffness: 120,
    damping: 18,
  });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-3, 3]), {
    stiffness: 120,
    damping: 18,
  });

  const onMouseMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onMouseLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-[100svh] flex-col overflow-hidden bg-[linear-gradient(180deg,#FFFFFF_0%,#F6FBFE_52%,#EAF5FC_100%)]"
    >
      {/* orbital arcs — subtle reference to the brand symbol */}
      <svg
        aria-hidden="true"
        viewBox="0 0 800 800"
        className="pointer-events-none absolute -right-48 top-1/2 hidden h-[56rem] w-[56rem] -translate-y-1/2 lg:block"
      >
        <g fill="none" stroke="rgba(11,94,168,0.10)" strokeWidth="1">
          <circle cx="400" cy="400" r="380" />
          <circle cx="400" cy="400" r="300" />
          <path d="M60 430 C 220 300, 560 300, 740 420" stroke="rgba(22,135,217,0.16)" />
        </g>
      </svg>
      <div
        aria-hidden="true"
        className="absolute -left-56 -top-56 h-[30rem] w-[30rem] rounded-full bg-softblue/70 lg:block"
      />

      <div className="mx-auto grid w-full max-w-[1600px] flex-1 items-center gap-14 px-6 pb-24 pt-40 md:px-12 lg:grid-cols-12 lg:gap-6 lg:pt-44">
        <motion.div style={{ y: copyY }} className="relative z-10 lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55, ease: EASE }}
            className="mb-8 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.32em] text-brand"
          >
            <span className="h-px w-8 bg-brand/60" aria-hidden="true" />
            Oswal Coslabs Private Limited
          </motion.div>

          <h1 className="font-serif font-medium leading-[0.98] tracking-tight text-navy">
            <MaskedLines
              inView={false}
              delay={0.7}
              stagger={0.16}
              lines={[
                <span key="a" style={{ fontSize: "clamp(3.2rem, 7.6vw, 7.4rem)" }} className="block">
                  QUALITY CARE<span className="text-brand">.</span>
                </span>,
                <span key="b" style={{ fontSize: "clamp(2.6rem, 6.2vw, 6rem)" }} className="block">
                  BEAUTY YOU CAN
                </span>,
                <span key="c" style={{ fontSize: "clamp(2.6rem, 6.2vw, 6rem)" }} className="block">
                  TRUST<span className="text-brand">.</span>
                </span>,
              ]}
            />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 26 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.3, ease: EASE }}
            className="mt-9 max-w-xl text-base leading-relaxed text-fog md:text-lg"
          >
            OSWAL COSLABS PRIVATE LIMITED is focused on skincare, beauty, personal
            care and baby care products, with a commitment to quality,
            affordability and customer satisfaction.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 26 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.45, ease: EASE }}
            className="mt-11 flex flex-wrap items-center gap-5"
          >
            <button
              type="button"
              onClick={() => scrollToId("products")}
              data-testid="hero-explore-cta"
              className="group flex items-center gap-3 border border-brand bg-brand px-9 py-4 text-[11px] font-semibold uppercase tracking-[0.22em] text-white transition-colors duration-300 hover:border-brand-deep hover:bg-brand-deep"
            >
              Explore products
              <ArrowRight
                size={15}
                className="transition-transform duration-300 group-hover:translate-x-1.5"
              />
            </button>
            <button
              type="button"
              onClick={() => scrollToId("about")}
              data-testid="hero-about-cta"
              className="group flex items-center gap-3 border border-navy/20 bg-white px-9 py-4 text-[11px] font-semibold uppercase tracking-[0.22em] text-navy transition-colors duration-300 hover:border-brand hover:text-brand"
            >
              About us
              <ArrowRight
                size={15}
                className="transition-transform duration-300 group-hover:translate-x-1.5"
              />
            </button>
          </motion.div>
        </motion.div>

        <div className="relative lg:col-span-5">
          <motion.div
            aria-hidden="true"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.6, delay: 0.9 }}
            className="animate-blue-drift absolute -inset-10 z-0 rounded-full bg-[radial-gradient(closest-side,rgba(22,135,217,0.22),transparent)] blur-2xl"
          />
          <motion.div
            style={{ y: imgY, rotateX, rotateY, transformPerspective: 1200 }}
            onMouseMove={onMouseMove}
            onMouseLeave={onMouseLeave}
            className="relative z-10 ml-auto w-full max-w-md lg:max-w-none"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.3, delay: 0.8, ease: EASE }}
              className="img-grade relative aspect-[4/5] overflow-hidden shadow-[0_40px_90px_-40px_rgba(6,59,104,0.35)]"
              data-testid="hero-image-main"
            >
              <img
                src={IMAGES.heroMain}
                alt="White cosmetic pump bottle with a folded towel and fresh flowers in soft studio light"
                className="h-full w-full object-cover"
                loading="eager"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.1, delay: 1.15, ease: EASE }}
              className="absolute -bottom-10 -left-6 z-20 w-36 border-[6px] border-white shadow-[0_24px_60px_-24px_rgba(6,59,104,0.4)] sm:w-44 md:-left-14"
              data-testid="hero-image-secondary"
            >
              <div className="animate-slow-float img-grade">
                <img
                  src={IMAGES.heroSecondary}
                  alt="Blue serum bottle with a dropper held in hand against a clean background"
                  className="aspect-square w-full object-cover"
                  loading="eager"
                />
              </div>
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1.7 }}
              className="mt-6 hidden text-right text-[10px] font-semibold uppercase tracking-[0.3em] text-fog lg:block"
            >
              Quality · Affordability · Trust
            </motion.p>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.9 }}
        className="absolute bottom-7 left-6 z-10 flex items-center gap-4 md:left-12"
        data-testid="hero-scroll-indicator"
      >
        <span className="text-[10px] font-semibold uppercase tracking-[0.34em] text-fog">
          Scroll to explore
        </span>
        <span className="relative h-12 w-px overflow-hidden bg-navy/10">
          <span className="animate-scroll-line absolute inset-0 bg-brand" />
        </span>
      </motion.div>
    </section>
  );
}
